import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Alert,
} from "@mui/material";
import { apiClient } from "../../utils/apiClient";
import type { WorkflowProgress } from "./useAdminWorkflowAction";

interface WorkflowProgressTrackerProps {
  recurringWorkflowId: string;
  pollInterval?: number;
}

export function WorkflowProgressTracker({
  recurringWorkflowId,
  pollInterval = 2000,
}: WorkflowProgressTrackerProps) {
  const [progress, setProgress] = useState<WorkflowProgress | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollState = useCallback(async () => {
    try {
      const data = await apiClient.get<{ state: WorkflowProgress; currentWorkflowId: string | null }>(
        `/api/workflows/recurring-workflows/${recurringWorkflowId}/state`,
      );

      if (data.state) {
        setProgress(data.state);
        if (data.state.progressPercent >= 1) {
          stopPolling();
          setIsComplete(true);
        } else if (data.currentWorkflowId === null) {
          stopPolling();
          setError(data.state.progress || "Workflow failed");
        }
      }
    } catch (err) {
      console.warn("[WorkflowProgressTracker] Polling error:", err);
    }
  }, [recurringWorkflowId, stopPolling]);

  useEffect(() => {
    pollState();
    pollTimerRef.current = setInterval(pollState, pollInterval);
    return stopPolling;
  }, [pollState, pollInterval, stopPolling]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (isComplete && progress) {
    return (
      <Alert severity="success">
        {progress.progress}
      </Alert>
    );
  }

  if (progress) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">{progress.progress}</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.round(progress.progressPercent * 100)}
        />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2">Starting...</Typography>
      </Box>
      <LinearProgress />
    </Paper>
  );
}
